// Consolidated route guard for reports. Handles:
//
//   1. Role gate — numeric level comparison (>=), matching the existing
//      RoleProtectedRoute pattern in router.tsx
//   2. Vertical gate — hide RESTAURANT reports from RETAIL tenants (and vice
//      versa in future) with a friendly "Not available for your business
//      type" screen
//   3. Feature flag gate — e.g. ENABLE_FESTIVE_MODULE for /reports/demand-forecast
//   4. Plan gate — a report not in the business's plan shows LockedFeature
//      (docs/plan-limits-plan.md §7). Checked last, so the lock only shows once
//      the report is otherwise open to this user.
//
// Platform roles (SUPER_ADMIN/ADMIN/MODERATOR) sit numerically above OWNER,
// so the `>=` comparison gives them access to all tenant reports by default
// — the same behavior as the other RoleProtectedRoute routes.

import type { ReactNode } from 'react';
import type { PlanFeatureKey } from '@bbpos/types';
import { useAuthStore } from '@/stores/use-auth-store';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { usePlanFeatures } from '@/hooks/use-plan-feature';
import { LockedFeature } from '@/components/features/subscriptions/LockedFeature';
import { EmptyState, PageContainer } from '@bbpos/ui';

export const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100,
  ADMIN: 80,
  MODERATOR: 60,
  OWNER: 50,
  MANAGER: 30,
  CASHIER: 5,
};

export type ReportVerticalScope = 'SHARED' | 'RESTAURANT' | 'RETAIL';

export interface ReportGuardProps {
  minRoleLevel: number;
  vertical?: ReportVerticalScope;
  featureFlag?: string;
  planFeature?: PlanFeatureKey;
  children: ReactNode;
}

function NotAvailable({ reason }: { reason: string }) {
  return (
    <PageContainer title="Report Unavailable" breadcrumbs={[{ label: 'Reports', href: '/reports' }, { label: 'Unavailable' }]}>
      <div className="rounded-2xl bg-gradient-to-br from-neutral-50 to-white dark:from-neutral-800 dark:to-neutral-900 shadow-md p-10 animate-fade-in">
        <EmptyState
          variant="page"
          title="Report not available"
          description={reason}
          icon={
            <svg className="h-16 w-16 text-neutral-300 dark:text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          }
        />
      </div>
    </PageContainer>
  );
}

function Forbidden() {
  return (
    <PageContainer title="Access Denied" breadcrumbs={[{ label: 'Reports', href: '/reports' }, { label: 'Access denied' }]}>
      <div className="rounded-2xl bg-gradient-to-br from-danger-50 to-white dark:from-danger-900/20 dark:to-neutral-900 shadow-md p-10 animate-fade-in">
        <EmptyState
          variant="page"
          title="403 — Access denied"
          description="Your role does not have permission to view this report. Please contact your administrator if you believe this is a mistake."
          icon={
            <svg className="h-16 w-16 text-danger-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          }
        />
      </div>
    </PageContainer>
  );
}

export function ReportGuard({ minRoleLevel, vertical, featureFlag, planFeature, children }: ReportGuardProps) {
  const profile = useAuthStore((s) => s.profile);
  const flags = useFeatureFlag();
  const hasPlanFeature = usePlanFeatures();

  const role = profile?.role ?? '';
  const level = ROLE_LEVELS[role] ?? 0;

  // 1. Role check — numeric >= comparison (matches router.tsx RoleProtectedRoute)
  if (level < minRoleLevel) {
    return <Forbidden />;
  }

  // 2. Vertical gate
  const tenantVertical = profile?.tenant?.business_type;
  if (
    vertical === 'RESTAURANT' &&
    tenantVertical &&
    tenantVertical !== 'RESTAURANT' &&
    level < 60 // platform roles bypass (they drill into any tenant via /tenants/:id)
  ) {
    return <NotAvailable reason="This report is only available for Restaurant businesses. Your tenant is configured as Retail." />;
  }
  if (
    vertical === 'RETAIL' &&
    tenantVertical &&
    tenantVertical !== 'RETAIL' &&
    level < 60
  ) {
    return <NotAvailable reason="This report is only available for Retail businesses." />;
  }

  // 3. Feature flag gate
  if (featureFlag && !flags[featureFlag]) {
    return <NotAvailable reason={`This report requires the ${featureFlag} feature to be enabled for your tenant.`} />;
  }

  // 4. Plan gate
  if (planFeature && !hasPlanFeature(planFeature)) {
    return <LockedFeature feature={planFeature} />;
  }

  return <>{children}</>;
}
